import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

const STORAGE_KEY = 'passcar-cookie-consent';

export interface CookiePreferences {
  necessary: true; // always true
  functional: boolean;
  analytics: boolean;
}

interface CookieConsentState extends CookiePreferences {
  hasConsented: boolean;
}

interface CookieConsentContextType extends CookieConsentState {
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (prefs: Omit<CookiePreferences, 'necessary'>) => void;
  openSettings: () => void;
  isSettingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
}

const defaultState: CookieConsentState = {
  necessary: true,
  functional: false,
  analytics: false,
  hasConsented: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

function loadFromStorage(): CookieConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...defaultState, ...parsed, necessary: true };
    }
  } catch { /* ignore */ }
  return defaultState;
}

function saveToStorage(state: CookieConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CookieConsentState>(loadFromStorage);
  const [isSettingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (state.hasConsented) {
      saveToStorage(state);
    }
  }, [state]);

  const acceptAll = useCallback(() => {
    const newState: CookieConsentState = {
      necessary: true,
      functional: true,
      analytics: true,
      hasConsented: true,
    };
    setState(newState);
    saveToStorage(newState);
  }, []);

  const rejectAll = useCallback(() => {
    const newState: CookieConsentState = {
      necessary: true,
      functional: false,
      analytics: false,
      hasConsented: true,
    };
    setState(newState);
    saveToStorage(newState);
  }, []);

  const savePreferences = useCallback((prefs: Omit<CookiePreferences, 'necessary'>) => {
    const newState: CookieConsentState = {
      necessary: true,
      functional: prefs.functional,
      analytics: prefs.analytics,
      hasConsented: true,
    };
    setState(newState);
    saveToStorage(newState);
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{
      ...state,
      acceptAll,
      rejectAll,
      savePreferences,
      openSettings,
      isSettingsOpen,
      setSettingsOpen,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
